import React from 'react';
import { Box, Tooltip } from '@material-ui/core';
import { TrendingUp, Info, Warning, Block } from '@material-ui/icons';
import { CapacityStatus } from '../hooks/useActivityCapacityStatus';

interface CapacityStatusChipProps {
  status: CapacityStatus;
  rosteredVolunteers?: number;
  maxVolunteers?: number | null;
  minVolunteers?: number;
  neededVolunteers?: number;
  size?: 'small' | 'tiny' | 'medium';
}

interface StatusConfig {
  label: string;
  textColor: string;
  bgColor: string;
  icon: React.ReactNode;
}

const getStatusConfig = (status: CapacityStatus, size: 'small' | 'tiny' | 'medium'): StatusConfig | null => {
  const iconSize = size === 'tiny' ? 10 : size === 'medium' ? 12 : 11;
  const iconStyle = { fontSize: iconSize, marginRight: 3 };

  const statusMap: Record<string, StatusConfig> = {
    FULL: {
      label: 'FULL',
      textColor: '#6B7280',
      bgColor: '#F3F4F6',
      icon: <Block style={iconStyle} />,
    },
    ALMOST_FULL: {
      label: 'ALMOST FULL',
      textColor: '#92400E',
      bgColor: '#FFFBEB',
      icon: <TrendingUp style={iconStyle} />,
    },
    SPOTS_OPEN: {
      label: 'SPOTS OPEN',
      textColor: '#1E40AF',
      bgColor: '#EFF6FF',
      icon: <Info style={iconStyle} />,
    },
    IN_NEED: {
      label: 'IN NEED',
      textColor: '#92400E',
      bgColor: '#FFFBEB',
      icon: <Warning style={iconStyle} />,
    },
  };

  if (!status) return null;
  return statusMap[status] || null;
};

const buildTooltip = (
  status: CapacityStatus,
  rosteredVolunteers?: number,
  maxVolunteers?: number | null,
  minVolunteers?: number,
  neededVolunteers?: number
): string => {
  if (rosteredVolunteers === undefined) return '';

  const parts: string[] = [];

  if (maxVolunteers !== null && maxVolunteers !== undefined) {
    const available = maxVolunteers - rosteredVolunteers;
    if (available > 0) {
      parts.push(`${available} spots available`);
    }
  } else {
    parts.push(`${rosteredVolunteers} rostered`);
  }

  if (status === 'IN_NEED') {
    const needed = neededVolunteers ?? (minVolunteers !== undefined ? minVolunteers - rosteredVolunteers : 0);
    if (needed > 0) {
      parts.push(`${needed} more needed`);
    }
  }

  return parts.join(', ');
};

export const CapacityStatusChip = ({
  status,
  rosteredVolunteers,
  maxVolunteers,
  minVolunteers,
  neededVolunteers,
  size = 'small',
}: CapacityStatusChipProps) => {
  const isTiny = size === 'tiny';
  const config = getStatusConfig(status, size);

  if (!config) return null;

  const tooltip = buildTooltip(status, rosteredVolunteers, maxVolunteers, minVolunteers, neededVolunteers);

  const chip = (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='center'
      style={
        size === 'medium'
          ? {
              color: config.textColor,
              backgroundColor: config.bgColor,
              border: `1px solid ${config.textColor}30`,
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              borderRadius: '6px',
              padding: '2px 8px',
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
            }
          : isTiny
          ? {
              color: config.textColor,
              backgroundColor: config.bgColor,
              fontSize: '9px',
              fontWeight: 600,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              borderRadius: '4px',
              padding: '1px 6px',
              lineHeight: 1.4,
            }
          : {
              color: config.textColor,
              backgroundColor: config.bgColor,
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              border: `1px solid ${config.textColor}30`,
              borderTop: 'none',
              borderRadius: '0 0 12px 12px',
              padding: '15px 12px 3px 12px',
              marginTop: '-13px',
              position: 'relative',
              zIndex: 0,
              lineHeight: 1.4,
            }
      }
    >
      {config.icon}
      <span>{config.label}</span>
    </Box>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow>
        {chip}
      </Tooltip>
    );
  }

  return chip;
};
